/**
 * calcBSMT — Basement / Hopper window cut list calculations
 *
 * All formulas verified line-by-line against BSMT DNA spreadsheet.
 * Excel cell references noted inline for auditing.
 *
 * WindowRow.jsx stores glass fields with prefix "Hopper":
 *   win.HopperGlassPkg   win.HopperGlassOpts
 *   win.HopperGridType   win.HopperGridPat   win.HopperGridV   win.HopperGridH
 *
 * Formula key (DNA sheet):
 *   B21  = indivW  (overallW ÷ numLites)
 *   C21  = indivH  (height)
 *   B19  = unitQty (qty × numLites)
 *   C50  = sashW_ref = B21-4.6875  (indivW-4.6875)
 *   D50  = sashH_ref = C21-3.5     (indivH-3.5)
 *
 * FIXES vs previous version:
 *  1.  sashW_ref / sashH_ref were SWAPPED — now correct per DNA Sash Size row
 *  2.  665 Width Reinf qty:  was B19*1,  now B19*2  (DNA: C31=B19*2)
 *  3.  665 Height Reinf qty: was B19*1,  now B19*4  (DNA: C32=B19*4)
 *  4.  PVC Trim length:      was indivH-3.875, now indivW-3.875 (DNA: =B21-3.875)
 *  5.  Sash Stops length:    was indivW-2.75,  now height-2.75  (DNA: =C12-2.75=overall height)
 *  6.  Glazbead qty:         was totalQty*2,   now totalQty*1   (DNA: C36=C37=B19*1)
 *  7.  Glazbead part:        now woodgrain logic (5457-glazbeadColor)
 *  8.  Starter part:         now woodgrain logic (8351-glazbeadColor)
 *  9.  Frame part numbers:   WHITE + fin-type logic per DNA IF chain
 *  10. Flex Screen dims:     W=indivH-4.3125, H=Full→indivW-3.125|Half→indivW/2-0.625
 *  11. Std Screen H qty:     threshold uses indivH (C21), not indivW
 *  12. Field names:          now reads HopperGlassPkg/Opts/GridType/Pat/V/H
 *  13. Grid fields:          fully wired with hasGrids, gridLenH/V, horizQty, vertQty
 */
export function calcBSMT(win) {
  const {
    // ── Dimensions ──────────────────────────────────────────────────────────
    width: overallW,
    height,
    // ── Identity ────────────────────────────────────────────────────────────
    config  = 'Single',
    color   = 'White',
    finType = 'Without Fin',
    qty     = 1,
    label   = '',
    // ── Screen ──────────────────────────────────────────────────────────────
    screenType = 'None',
    screenSize = 'Full Scrn',
    meshType   = 'STD Mesh',
    // ── Gas ─────────────────────────────────────────────────────────────────
    gasFill = 'Argon',
    // ── Glass — BSMT uses Hopper prefix ─────────────────────────────────────
    HopperGlassPkg  = 'DLA',
    HopperGlassOpts = 'None',
    HopperGridType  = 'None',
    HopperGridPat   = 'None',
    HopperGridV     = '0',
    HopperGridH     = '0',
  } = win;

  // ── Color helpers ─────────────────────────────────────────────────────────
  const colorLower  = (color || '').toLowerCase();
  const isWhite     = colorLower === 'white';
  const isDark      = colorLower.includes('woodgrain');
  const withFin     = finType === 'With Fin' || finType === 'With Fin & J';

  // Woodgrain glazbead/part color logic:
  // IF(color="dk woodgrain/white","White",IF(color="dk woodgrain/clay","Clay",color))
  const glazbeadColor = colorLower.includes('white') ? 'White'
                      : colorLower.includes('clay')  ? 'Clay'
                      : color;

  // ── Lite multiplier ───────────────────────────────────────────────────────
  const liteMap = {
    Single:   1,
    '2-Lite': 2,
    '3-Lite': 3,
    '4-Lite': 4,
    '5-Lite': 5,
    'Q/H/Q':  2,
    Twin: 2, Triple: 3, Quad: 4, Quin: 5,
  };
  const numLites = liteMap[config] || 1;
  const indivW   = overallW / numLites;   // B21
  const indivH   = height;                // C21
  const unitQty  = qty * numLites;        // B19

  // ── Sash reference sizes (DNA Sash Size row) ──────────────────────────────
  // Sash Size: W=B21-4.6875,  H=C21-3.5
  // C50 = sashW_ref = B21-4.6875 = indivW-4.6875
  // D50 = sashH_ref = C21-3.5   = indivH-3.5
  const sashW_ref = indivW - 4.6875;     // C50
  const sashH_ref = indivH - 3.5;        // D50

  // ── Glass ─────────────────────────────────────────────────────────────────
  // DNA Glass row:
  //   Qty    = B19*1   (unitQty)
  //   Width  = C50-2   = sashW_ref-2 = indivW-6.6875
  //   Height = D50-2   = sashH_ref-2 = indivH-5.5
  const glassW = sashW_ref - 2;          // C50-2 = indivW-6.6875
  const glassH = sashH_ref - 2;          // D50-2 = indivH-5.5

  // ── Grid helper ───────────────────────────────────────────────────────────
  // GridLenH = glassW-1  (DNA: =L10-1)
  // GridLenV = glassH-1  (DNA: =L11-1)
  // Horizontals = gridH × unitQty  (DNA: =M17*L9)
  // Verticals   = gridV × unitQty  (DNA: =L17*L9)
  const gridV    = parseInt(HopperGridV) || 0;
  const gridH    = parseInt(HopperGridH) || 0;
  const hasGrids = !!HopperGridType
                && HopperGridType !== 'None'
                && HopperGridType !== ''
                && (gridV > 0 || gridH > 0);

  const gridLenH  = hasGrids ? glassW - 1 : null;
  const gridLenV  = hasGrids ? glassH - 1 : null;
  const horizQty  = hasGrids ? gridH * unitQty : 0;
  const vertQty   = hasGrids ? gridV * unitQty : 0;
  // Grid color matches INSIDE-visible frame color.
  // Frame naming: "Inside/Outside" (e.g. "Dk Woodgrain/White" = dark inside, white outside).
  // Inside grid face matches inside frame color; outside grid face is always White.
  // We report the INSIDE color so Tracy Corners knows what to build.
  const insideGridColor = color.includes('/')
    ? color.split('/')[0].trim()
    : color;
  const gridColor = hasGrids ? insideGridColor : '';

  // ── Frame part numbers ────────────────────────────────────────────────────
  // DNA logic (WHITE windows use fin-type switching, all others use base number):
  //   Heights: IF(WHITE, IF(with fin,"5152-color", IF(without fin,"5452-color")), "5152-color")
  //   Header:  IF(WHITE, IF(with fin,"5150-color", IF(without fin,"5450-color")), "5150-color")
  //   Sill:    IF(WHITE, IF(with fin,"5191-color", IF(without fin,"5192-color")), "5191-color")
  const frameHeightPart = isWhite
    ? (withFin ? `5152-${color}` : `5452-${color}`)
    : `5152-${color}`;
  const frameHeaderPart = isWhite
    ? (withFin ? `5150-${color}` : `5450-${color}`)
    : `5150-${color}`;
  const frameSillPart = isWhite
    ? (withFin ? `5191-${color}` : `5192-${color}`)
    : `5191-${color}`;

  // ── Frame lengths ─────────────────────────────────────────────────────────
  // BSMT Frame Heights: qty=B19*2, length=C21+3.375 = indivH+3.375
  // BSMT Frame Header:  qty=B19*1, length=B21+1.5   = indivW+1.5
  // BSMT Frame Sill:    qty=B19*1, length=B21+1.5   = indivW+1.5
  const frameHeightLen = indivH + 3.375; // C21+3.375
  const frameHorizLen  = indivW + 1.5;   // B21+1.5

  // ── Sill Extension ────────────────────────────────────────────────────────
  // Sill Extension: qty=B19*1, length=B21-2.75=indivW-2.75, part=5131-color
  const sillExtLen = indivW - 2.75;

  // ── Sash ──────────────────────────────────────────────────────────────────
  // Sash Heights: qty=B19*2, length=D50+0.6875 = sashH_ref+0.6875 = indivH-2.8125
  // Sash Widths:  qty=B19*2, length=C50+0.6875 = sashW_ref+0.6875 = indivW-4.0
  const sashHCut = sashH_ref + 0.6875;  // D50+0.6875 = indivH-2.8125
  const sashWCut = sashW_ref + 0.6875;  // C50+0.6875 = indivW-4.0

  // ── Reinforcements ────────────────────────────────────────────────────────
  // 665 Width Reinf:  qty=B19*2, length=B21-8=indivW-8,   part=7913 (no color)
  // 665 Height Reinf: qty=B19*4, length=C21-7.5=indivH-7.5, part=7913
  const reinfW = indivW - 8.0;
  const reinfH = indivH - 7.5;

  // ── PVC Trim ──────────────────────────────────────────────────────────────
  // PVC Trim Board 3/4 X 1 5/8: qty=B19*1, length=B21-3.875=indivW-3.875, part=PVC
  // DNA: =B21-3.875  (width, NOT height)
  const pvcTrimLen = indivW - 3.875;    // B21-3.875

  // ── Sash Stops ────────────────────────────────────────────────────────────
  // (x2) Sash Stops: qty=B19*1, length=C12-2.75=height-2.75, part=8483-color
  // DNA: =C12-2.75 where C12 = original overall height input
  const sashStopsLen = height - 2.75;   // C12-2.75 = overall height

  // ── Glazbead ─────────────────────────────────────────────────────────────
  // (x2) Glazbead Heights: qty=B19*1, length=D50-2.8125=sashH_ref-2.8125=indivH-6.3125
  // (x2) Glazbead Width:   qty=B19*1, length=C50-1.6875=sashW_ref-1.6875=indivW-6.375
  // Part: woodgrain logic → 5457-glazbeadColor
  const glazbeadH    = sashH_ref - 2.8125;  // D50-2.8125 = indivH-6.3125
  const glazbeadW    = sashW_ref - 1.6875;  // C50-1.6875 = indivW-6.375
  const glazbeadPart = `5457-${glazbeadColor}`;

  // ── Starter ───────────────────────────────────────────────────────────────
  // DNA: qty=IF(without fin,B19*1,IF(with fin,B19*0))
  //      length=B12=overallW
  //      part: woodgrain logic → 8351-glazbeadColor
  const starterQty  = withFin ? 0 : unitQty;
  const starterPart = `8351-${glazbeadColor}`;

  // ── Snap Mull ─────────────────────────────────────────────────────────────
  // qty: single→0, twin→D12*1=qty, triple→D12*2, quad→D12*3, quin→D12*4, q/h/q→D12*2
  // length=C21-0.125=indivH-0.125, part=8309-color
  const snapMullQtyMap = {
    Single:0, '2-Lite':1, '3-Lite':2, '4-Lite':3, '5-Lite':4, 'Q/H/Q':2,
    Twin:1, Triple:2, Quad:3, Quin:4,
  };
  const snapMullQty = (snapMullQtyMap[config] || 0) * qty;
  const snapMullLen = indivH - 0.125;   // C21-0.125

  // ── Flex Screen ───────────────────────────────────────────────────────────
  // DNA: Flex Screen row uses B49/C49/D49 which map to frame size:
  //   Frame Size: C21=indivH (col 2), B21=indivW (col 1)
  //   D49-4.3125  → indivH-4.3125  (this is the HEIGHT column in DNA = width dimension)
  //   Full Scrn:  C49-3.125  → indivW-3.125
  //   Half Scrn:  C49/2-0.625 → indivW/2-0.625
  // Note: BSMT is a hopper — opens vertically, so "width" on screen = frame height
  const isFlexScrn = screenType === 'Flex Scrn';
  const isStdScrn  = screenType === 'Std Scrn';

  const flexScreenW = indivH - 4.3125;  // D49-4.3125
  const flexScreenH = screenSize === 'Half Scrn'
    ? indivW / 2 - 0.625               // C49/2-0.625
    : indivW - 3.125;                  // C49-3.125

  // ── Std Screen ────────────────────────────────────────────────────────────
  // Screen Widths:            qty=B19*2, length=B21-6.3125=indivW-6.3125
  // Screen Heights with Pull: qty=IF(C21<50,B19*1,B19*2), length=C21-4.875=indivH-4.875
  // Screen Heights W/O Pull:  qty=IF(C21<50,B19*1,0),     length=C21-4.875
  // Note: threshold is C21=indivH (NOT indivW)
  const stdScreenW         = indivW - 6.3125;
  const stdScreenH         = indivH - 4.875;
  const stdScreenWQty      = isStdScrn ? unitQty * 2 : 0;
  const stdScreenPullQty   = isStdScrn ? (indivH < 50 ? unitQty : unitQty * 2) : 0;
  const stdScreenNoPullQty = isStdScrn ? (indivH < 50 ? unitQty : 0) : 0;

  const screenPart     = isDark ? `SCRNPLN-${glazbeadColor}`  : `SCRNPLN-${color}`;
  const screenPullPart = isDark ? `SCRNPULL-${glazbeadColor}` : `SCRNPULL-${color}`;

  const flexScreen = isFlexScrn ? {
    qty:       unitQty,
    width:     flexScreenW,
    height:    flexScreenH,
    meshType:  meshType || 'STD Mesh',
    screenType,
    label,
  } : null;

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    label,
    type:      'BSMT',
    color,
    overallW,
    width:     overallW,
    height,
    config:    config || 'Single',
    qty,
    totalQty:  unitQty,
    indivW,
    finType,
    screenType,
    screenSize,

    // ── Glass ────────────────────────────────────────────────────────────────
    glass: [
      {
        label:       'Hopper Glass',
        qty:         unitQty,               // B19*1
        width:       glassW,                // C50-2 = indivW-6.6875
        height:      glassH,                // D50-2 = indivH-5.5
        glassType:   HopperGlassPkg,
        glassConfig: HopperGlassOpts !== 'None' ? HopperGlassOpts : '',
        gasFill,
        grids:       hasGrids,
        gridType:    hasGrids ? HopperGridType : '',
        gridPattern: hasGrids ? (HopperGridPat === 'None' ? '' : HopperGridPat) : '',
        gridQtyV:    hasGrids ? gridV : 0,
        gridQtyH:    hasGrids ? gridH : 0,
        gridColor:   hasGrids ? gridColor : '',
        gridLenH,
        gridLenV,
        horizQty,
        vertQty,
      },
    ],

    // ── Frame ────────────────────────────────────────────────────────────────
    frame: [
      { label: 'BSMT Frame Heights', qty: unitQty * 2, length: frameHeightLen, part: frameHeightPart }, // B19*2
      { label: 'BSMT Frame Header',  qty: unitQty,     length: frameHorizLen,  part: frameHeaderPart }, // B19*1
      { label: 'BSMT Frame Sill',    qty: unitQty,     length: frameHorizLen,  part: frameSillPart   }, // B19*1
      { label: 'Sill Extension',     qty: unitQty,     length: sillExtLen,     part: `5131-${color}` }, // B19*1
    ],

    // ── Sash ─────────────────────────────────────────────────────────────────
    sash: [
      { label: 'Sash Heights', qty: unitQty * 2, length: sashHCut, part: `5540-${color}` }, // B19*2
      { label: 'Sash Widths',  qty: unitQty * 2, length: sashWCut, part: `5540-${color}` }, // B19*2
    ],

    // ── Reinforcements ────────────────────────────────────────────────────────
    reinforcement: [
      { label: '665 Width Reinf',  qty: unitQty * 2, length: reinfW, part: '7913' }, // B19*2
      { label: '665 Height Reinf', qty: unitQty * 4, length: reinfH, part: '7913' }, // B19*4
    ],

    // ── Glazbead ─────────────────────────────────────────────────────────────
    glazbead: [
      { label: '(x2) Glazbead Heights', qty: unitQty, length: glazbeadH, part: glazbeadPart }, // B19*1
      { label: '(x2) Glazbead Width',   qty: unitQty, length: glazbeadW, part: glazbeadPart }, // B19*1
    ],

    // ── Misc ─────────────────────────────────────────────────────────────────
    misc: [
      { label: 'PVC Trim Board 3/4 X 1 5/8', qty: unitQty,     length: pvcTrimLen,   part: 'PVC'            }, // B19*1
      { label: '(x2) Sash Stops',             qty: unitQty,     length: sashStopsLen, part: `8483-${color}`  }, // B19*1
      ...(starterQty > 0 ? [
        { label: 'Starter',      qty: starterQty,  length: overallW,    part: starterPart          },
      ] : []),
      ...(snapMullQty > 0 ? [
        { label: '(x2) Snap Mull', qty: snapMullQty, length: snapMullLen, part: `8309-${color}` },
      ] : []),
    ],

    // ── Screens ───────────────────────────────────────────────────────────────
    flexScreen,
    stdScreen: isStdScrn ? {
      width:       stdScreenW,
      height:      stdScreenH,
      widthQty:    stdScreenWQty,
      withPullQty: stdScreenPullQty,
      noPullQty:   stdScreenNoPullQty,
      screenPart,
      screenPullPart,
      label,
    } : null,

    // ── Summary / reference sizes ─────────────────────────────────────────────
    summary: {
      sashWidth:  sashW_ref,    // C50 = indivW-4.6875
      sashHeight: sashH_ref,    // D50 = indivH-3.5
      glassW,
      glassH,
    },
  };
}